import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { TFunction, Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import useDraft from '../../../util/useDraft';
import { useActions } from '../../../Actions';
import { useValidatedSchema, ValidationSchema } from '../../../util/useValidatedSchema';
import {
    getAccountNumberValidator,
    getOrgNumberValidator,
    validateISO_3166_1,
    validateRequired
} from '../../../helpers/validation';
import { useAccount } from '../../account/hooks';
import { accountCurrencies } from '../../account/helpers';
import { validateValues as LocationFormValidator } from '../validation/SellerLocationFormValidation';


import { Button, ButtonExternalLink } from '../../../components/Buttons';
import { ABreakAll } from '../../../components/A';
import { Label, P } from '../../../components/Typography';
import Delay from '../../../components/Delay';

import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import LinearProgress from '@mui/material/LinearProgress';
import { useAccountIds } from '../../../auth/accessToken/withAccountIds';
import EnvironmentDropdown from '../../components/EnvironmentDropdown';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import styled from 'styled-components/macro';
import { getGeoLocation, getPhonePrefixFromCountry, mapValues } from '../../locations/shared/utils';
import { LocationFormValues } from '../../locations/types';
import Plus from '../../../components/Icons/plus';
import { getCountryOptions, getFastTrackOptions, getTypeOptions } from '../../locations/shared/options';
import SellerLocationForm from './SellerLocationForm';
import { approvalsStateSelector } from '../../approvalsSelectors';
import { ApprovalsPayoutDestinationResponse } from '../../../types/management-auth/generated';
import { fallbackIfEmpty } from '../../helpers';


interface AddLocationFormValues extends LocationFormValues {
}

type NewPayoutDestinationWithNewBankAccountValues = {
    accountId: string;
    payout_destination_id: string;
    payout_reference: string;
    name: string;
    description: string;
    // used for first bank account
    country_code: string;
    organization_number: string;
    bank_account_number: string;
    currency: string;
    locations: AddLocationFormValues[];
}


const createValidationSchema = (
    t: TFunction<('app' | 'payout')[], undefined>
): ValidationSchema<NewPayoutDestinationWithNewBankAccountValues> => ({
    payout_destination_id: [
        validateRequired(
            t('payout:general.validations.field.required', {
                name: t('payout:payout_destination.payout_destination_id'),
            })
        ),
        (value) => /^([\w\d\-_\S]{1,40})$/.test(value)
            ? undefined
            : t('payout:general.validations.field.invalid', {
                name: t('payout:payout_destination.payout_destination_id'),
            }),
    ],
    country_code: [
        validateRequired(
            t('payout:general.validations.field.required', {
                name: t('payout:bank_account.country_code'),
            })
        ),
        validateISO_3166_1(
            t('payout:general.validations.field.invalid', {
                name: t('payout:bank_account.country_code'),
            })
        ),
    ],
    currency: [
        (value, adjacent) => adjacent.bank_account_number.length > 0 ? validateRequired(
            t('payout:general.validations.field.required', {
                name: t('payout:general.currency'),
            })
        )(value) : undefined,
        (value, adjacent) => adjacent.bank_account_number.length === 0 || /^[A-Z]{3}$/.test(value)
            ? undefined
            : t('payout:general.validations.field.invalid', {
                name: t('payout:general.currency'),
            }),
    ],
    bank_account_number: [
        (value, adjacent) => adjacent.bank_account_number.length > 0 ? validateRequired(
            t('payout:general.validations.field.required', {
                name: t('payout:bank_account.number'),
            })
        )(value) : undefined,
        (value, adjacent) => adjacent.bank_account_number.length > 0 && adjacent.country_code ? getAccountNumberValidator(
            adjacent.country_code.toUpperCase(),
            t('payout:general.validations.field.invalid', {
                name: t('payout:bank_account.number'),
            })
        )(value) : undefined,
    ],
    organization_number: [
        validateRequired(
            t('payout:general.validations.field.required', {
                name: t('payout:bank_account.owner_orgno'),
            })
        ),
        (value, adjacent) => adjacent.country_code ? getOrgNumberValidator(
            adjacent.country_code.toUpperCase(),
            t('payout:general.validations.field.invalid', {
                name: t('payout:bank_account.owner_orgno'),
            })
        )(value) : undefined,
    ],
    locations: [
        (value) => {
            const locations = value as unknown as AddLocationFormValues[];
            if (locations && locations.length > 0) {
                return locations.reduce((a, location) => {
                    const errors = LocationFormValidator(location, t);
                    if ('locationId' in errors) {
                        return errors.locationId;
                    }
                    if ('country' in errors) {
                        return errors.country;
                    }
                    return a;
                }, '');
            } else {
                return undefined;
            }
        },
    ],
});

export type CreatePayoutDestinationDialogProps = Pick<DialogProps, 'open' | 'fullScreen' | 'fullWidth' | 'maxWidth'> & {
    onClose: () => void;
    environment: 'test' | 'prod';
    allowCreateProdPayoutDestinations: boolean;
};


const selector = (prodAccountId: string, testAccountId: string) => createSelector(
    approvalsStateSelector(prodAccountId),
    approvalsStateSelector(testAccountId),
    (prodState, testState) => ({
        isPostingProdSeller: prodState.loading,
        isPostingTestSeller: testState.loading,
    })
);

const quickerVersionQueryString = () => {
    const queryString = new URLSearchParams(window.location.search);
    const quickrVersion = queryString.get('quickr_version');
    if (quickrVersion) {
        return `quickr_version=${quickrVersion}`;
    }
    return `quickr_version=2`;
};

const getDefaultCountryCurrency = (country: string) => {
    const mapping: Record<string, string> = {
        'NO': 'NOK',
        'SE': 'SEK',
    };
    return mapping[country] || '';
};


const CreatePayoutDestinationDialog = ({
    onClose,
    allowCreateProdPayoutDestinations,
    environment,
    ...props
}: CreatePayoutDestinationDialogProps) => {
    const { t, i18n } = useTranslation(['app', 'payout']);
    const accountIds = useAccountIds();
    const { postSellerApproval } = useActions('management.approvals');
    const createLocation = useActions('settings.locations').createLocation;
    const { account } = useAccount();
    const country = account?.company?.address?.country;
    const currencies = fallbackIfEmpty(account ? accountCurrencies(account) :[], [getDefaultCountryCurrency(country || '')]);
    const [isFastTrack, setIsFastTrack] = useState(false);

    const countryOptions = getCountryOptions(t, country || '');
    const typeOptions = getTypeOptions(t);
    const fastTrackOptions = getFastTrackOptions(t);

    const [postedApprovalSeller, setPostedSeller] = useState<ApprovalsPayoutDestinationResponse>();

    const {
        isPostingProdSeller,
        isPostingTestSeller,
    } = useSelector(selector(accountIds.prodAccountId || '', accountIds.testAccountId || ''));



    const initialAccountId =  environment === 'prod' ? accountIds.prodAccountId : accountIds.testAccountId;
    const {
        setValue,
        combined,
        dirty,
        isTouched,
        clear,
    } = useDraft<NewPayoutDestinationWithNewBankAccountValues>({
        accountId: initialAccountId || '',
        payout_destination_id: '',
        payout_reference: account?.company.display_name || account?.company.business_name || '',
        name: '',
        description: '',
        country_code: country || '',
        organization_number: '',
        bank_account_number: '',
        currency: currencies[0] || getDefaultCountryCurrency(country || ''),
        locations: [],
    });
    const {
        hasErrors,
        result: errors,
        validateValues,
    } = useValidatedSchema({
        schema: useMemo(() => createValidationSchema(t), [t]),
        value: combined,
    });

    const onDialogClose = () => {
        onClose();
        // Reset form after it has been closed
        window.setTimeout(() => {
            clear();
            setPostedSeller(undefined);
        }, 300);
    };

    const isPostingSeller = isPostingProdSeller || isPostingTestSeller;

    const onSave = async () => {
        if (isPostingSeller || hasErrors || !!postedApprovalSeller) return;
        if (combined.locations.length > 0) {
            if (combined.locations.some(l => !(/^([\w\d\-_\S]{1,40})$/.test(l.locationId)) && l.country.value === '')) return;
            for (const l of combined.locations) {
                const location = mapValues(l);
                const geoLocation = await getGeoLocation(location.address!, i18n.language);

                await createLocation({
                    ...location,
                    address: {
                        ...location.address!,
                        ...(geoLocation || {}),
                    },
                }, l.accountId);
            }
        }

        const result = await postSellerApproval(combined.accountId, {
            payout_destination_id: combined.payout_destination_id,
            payout_reference: combined.payout_reference,
            payout_destination_name: combined.name,
            payout_destination_description: combined.description,
            country_code: combined.country_code.toUpperCase(),
            organization_number: combined.organization_number.replace(/\D/g, ''),
            bank_accounts: [{
                bank_name: '',
                bank_account_number: '',
                bank_account_currency: combined.currency,
                payout_currency: combined.currency,
            }],
        }, quickerVersionQueryString());

        setPostedSeller(result || undefined);
    };
    const onChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setValue(e.currentTarget.name, e.currentTarget.value);
    }, [setValue]);

    const getFieldProps = (key: keyof NewPayoutDestinationWithNewBankAccountValues): Partial<TextFieldProps> => {
        const touched = isTouched(key);
        const error = errors[key];
        return {
            name: key,
            value: combined[key],
            onChange,
            error: touched && !!error,
            helperText: touched ? error : ' ',
            fullWidth: true,
            required: !postedApprovalSeller,
            InputProps: {
                disabled: !!postedApprovalSeller,
                disableUnderline: !!postedApprovalSeller,
            },
        };
    };

    const creatableAccountIds = accountIds.accountIds.filter(aid => {
        return allowCreateProdPayoutDestinations || !aid.startsWith('P');
    });

    const addNewLocation = () => {
        setValue('locations', [
            ...combined.locations,
            {
                accountId: combined.accountId,
                country: {
                    label: country === 'NO'
                        ? t('app:locations.location_form.fields.norway')
                        : t('app:locations.location_form.fields.sweden'),
                    value: country,
                },
                addressLine: '',
                addressLine2: '',
                businessName: '',
                countryPrefix: getPhonePrefixFromCountry(country),
                phoneNumber: '',
                email: '',
                franchise: false,
                franchiseName: '',
                latitude: 0,
                longitude: 0,
                name: '',
                postalCode: '',
                postalPlace: '',
                websiteUrl: '',
                type: typeOptions[0],
                city: '',
                organizationNumber: combined.organization_number.replace(/\s/g, ''),
            }]);
    };

    return (
        <Dialog
            {...props}
        >
            <DialogTitle>
                {t('payout:payout_destination.form.title')}
            </DialogTitle>
            <DialogContent sx={{ ...(!postedApprovalSeller && { paddingBottom: 0 }), ...(isPostingSeller && { opacity: .5, pointerEvents: 'none' }) }}>
                {!postedApprovalSeller && <DialogContentText sx={{ whiteSpace: 'pre-wrap' }}>
                    {t('payout:payout_destination.form.description')}
                </DialogContentText>}
                {postedApprovalSeller && <Grid container spacing={2} sx={{ marginTop: 2 }}>
                    <PostedSellerApproval postedApprovalSeller={postedApprovalSeller} isFastTrack />
                </Grid>}
                {!postedApprovalSeller && <>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={24} sm={12}>
                            <EnvironmentDropdown
                                accountIds={creatableAccountIds}
                                name="accountId"
                                value={combined['accountId']}
                                label={t('payout:payout_destination.form.environment')}
                                placeholder=""
                                onChange={(value) => {
                                    setValue('accountId', value);
                                    setValue('bank_account_number', '');
                                    setValue('organization_number', '');
                                    if (combined.locations && combined.locations.length > 0) {
                                        combined.locations.forEach(l => l.accountId = value);
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputLabel sx={{ marginBottom: 0 }}
                                id={t('app:locations.location_form.fields.country')}>{t('app:locations.location_form.fields.country')}</InputLabel>
                            <Select
                                labelId={t('app:locations.location_form.fields.country')}
                                name="country_code"
                                value={combined['country_code']}
                                fullWidth
                                sx={{
                                    minWidth: 300,
                                    marginTop: '0px !important',
                                    '.MuiSelect-select': {
                                        padding: '6px 0',
                                    },
                                }}
                                onChange={(value) => {
                                    setValue('country_code', value);
                                }}
                            >
                                <MenuItem value={countryOptions[0].value}>{countryOptions[0].label}</MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label={t('payout:bank_account.owner_orgno')}
                                {...getFieldProps('organization_number')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputLabel required sx={{ marginBottom: 0 }}
                                id={t('payout:general.currency')}>{t('payout:general.currency')}</InputLabel>
                            <Select
                                required
                                value={combined.currency}
                                labelId={t('payout:general.currency')}
                                onChange={(selected) => setValue('currency', selected)}
                                fullWidth
                                sx={{
                                    minWidth: 300,
                                    marginTop: '0px !important',
                                    '.MuiSelect-select': {
                                        padding: '6px 0',
                                    },
                                }}
                            >
                                {currencies.map((currency) => (
                                    <MenuItem key={currency} value={currency}>
                                        {currency}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label={t('payout:payout_destination.form.payout_destination_id')}
                                {...getFieldProps('payout_destination_id')}
                            />
                        </Grid>
                        <Spacer /> 
                        {combined.accountId.startsWith('T') && <>
                            <Grid item xs={12} sm={12}>
                                <InputLabel sx={{ marginBottom: 0 }}
                                    id={t('payout:payout_destination.form.fast_track_title')}>{t('payout:payout_destination.form.fast_track_title')}</InputLabel>
                            </Grid>
                            <Grid item>
                                <Trans i18nKey={'payout:payout_destination.form.fast_track_description'}>
                                    <b>
                                        {' '}
                                    </b>
                                </Trans>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Switch checked={isFastTrack} onChange={() => {
                                    isFastTrack ? setValue('description', ''): setValue('description', fastTrackOptions[0].value);
                                    setIsFastTrack(!isFastTrack);
                                }}/>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ marginTop: 2 }}>
                                <Select
                                    disabled={!isFastTrack}
                                    value={combined.description}
                                    labelId={t('payout:payout_destination.form.fast_track_description')}
                                    onChange={(event) => setValue('description', event.target.value)}
                                    fullWidth
                                    sx={{
                                        minWidth: 300,
                                        marginTop: '0px !important',
                                        '.MuiSelect-select': {
                                            padding: '6px 0',
                                        },
                                    }}
                                >
                                    {fastTrackOptions.map((description) => (
                                        <MenuItem key={description.value} value={description.value}>
                                            {description.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                        </>} 
                    </Grid>
                    <Spacer />
                    <Label>{t('payout:payout_destination.form.add_locations_title')}</Label>
                    <Trans i18nKey={'payout:payout_destination.form.add_locations_desc'}>
                        <b>
                            {' '}
                        </b>
                    </Trans>
                    <br />
                    <br />
                    {combined.locations && combined.locations.length > 0 &&
                        combined.locations.map((location, index) => (<SellerLocationForm key={index}
                            onDelete={() => {
                                setValue('locations', combined.locations?.filter((l, i) => i !== index));
                            }}
                            locationFormValues={location}
                            onSubmit={(values) => setValue(`locations[${index}]`, values)}
                            onBlur={(values) => {
                                setValue(`locations[${index}]`, values);
                                validateValues();
                            }}
                            accountIds={creatableAccountIds} />))}
                    <Button style={{ marginTop: 32 }} onClick={addNewLocation} className="alt">
                        <Plus />
                        {t('payout:payout_destination.form.add_location_button')}
                    </Button>
                    <Spacer />
                </>}
            </DialogContent>
            <Delay show={isPostingSeller}>
                <LinearProgress variant="query" />
            </Delay>
            <DialogActions sx={{ ...(!postedApprovalSeller && { marginTop: 4 }) }}>
                {!postedApprovalSeller ? (<>
                    <Button
                        onClick={onSave}
                        disabled={hasErrors || !dirty || isPostingSeller}
                    >
                        {t('payout:payout_destination.form.submit')}
                    </Button>
                    <Button
                        className="alt"
                        onClick={onDialogClose}
                        disabled={isPostingSeller}
                    >
                        {t('payout:payout_destination.form.cancel')}
                    </Button>
                </>) : (<>
                    <Button
                        onClick={onDialogClose}
                    >
                        {t('payout:payout_destination.form.close')}
                    </Button>
                </>)}
            </DialogActions>
        </Dialog>
    );
};


const PostedSellerApproval = ({ postedApprovalSeller, isFastTrack }: { postedApprovalSeller: ApprovalsPayoutDestinationResponse; isFastTrack: boolean }) => {
    const { t } = useTranslation(['payout', 'app']);
    const { addMessage } = useActions('app_messages');
    const contractUrl = (postedApprovalSeller.links || []).find(l => l.rel === 'contract_url')?.href;
    const isPostedFastTrack = isFastTrack && ['AUTO_APPROVE', 'AUTO_DECLINE', 'AUTO_WAIT_FOR_SIGNATURE'].includes(postedApprovalSeller.payout_destination_description || '');
    const showContactUrl = contractUrl && !isPostedFastTrack;
    return (
        <Grid item xs={12} sx={{ marginTop: -4 }}>
            {isPostedFastTrack ?
                <Alert severity="success">
                    {t('payout:payout_destination.form.fast_track_submitted')}
                </Alert>
                :
                <Alert severity="success">
                    <AlertTitle>
                        {t('payout:payout_destination.form.new_bank_account.alert_link')}
                    </AlertTitle>
                    {contractUrl ? <>
                        <ABreakAll
                            href={contractUrl}
                            target="_blank"
                        >
                            {contractUrl}
                        </ABreakAll>
                    </> : <P>{t('app:settings.account.payout.application_link_missing')}</P>
                    }
                </Alert>
            }
            {showContactUrl &&
                    <Row sx={{ marginTop: 4 }}>
                        <ButtonExternalLink
                            onClick={async () => {
                                try {
                                    await window.navigator.clipboard.writeText(contractUrl || '');
                                    await addMessage({
                                        severity: 'info',
                                        text: t('payout:payout_destination.form.link_copied'),
                                    });
                                } catch {
                                }
                            }}
                            className="alt">
                            {t('payout:payout_destination.form.copy_link')}
                        </ButtonExternalLink>
                        <ButtonExternalLink
                            href={contractUrl}
                            target="_blank" rel="noreferrer"
                            className="alt">
                            {t('payout:payout_destination.form.open_declaration_form')}
                        </ButtonExternalLink>
                    </Row>
            }   
        </Grid>);
};


const Row = styled(Box)`
  > * {
    margin-right: 8px;
  }
`;

const Spacer = styled(Divider)`
  margin: 32px auto;
`;

export default CreatePayoutDestinationDialog;
